import React, { useEffect, useMemo, useState } from "react";
import { API_BASE } from "../api/config";
import "./WarehouseTransferHistoryPanel.css";

function fmtDate(value) {
  if (!value) return "-";
  return new Date(value).toLocaleString();
}

function fmtQty(value) {
  return Number(value || 0).toLocaleString();
}

function statusClass(status) {
  const s = String(status || "").toUpperCase();
  if (s.includes("DISPATCH")) return "isDispatched";
  if (s.includes("ACCEPT") || s.includes("RECEIVE") || s.includes("COMPLETED")) return "isDone";
  if (s.includes("APPROVED")) return "isApproved";
  return "";
}


function normValue(value) {
  return String(value ?? "").trim();
}

function normKeyValue(value) {
  return normValue(value).toUpperCase();
}

function itemKeyFromParts(code, color, size, brand = "") {
  return [
    normKeyValue(code),
    normKeyValue(color),
    normKeyValue(size),
    normKeyValue(brand),
  ].join("|");
}

function itemLabelFromParts(code, color, size, brand = "") {
  return [
    normValue(code) || "-",
    normValue(color) || "-",
    normValue(size) || "-",
    normValue(brand) || "-",
  ].join(" / ");
}

function addQty(map, key, qty) {
  map.set(key, Number(map.get(key) || 0) + Number(qty || 0));
}

function getIssueExpectedKey(row) {
  return itemKeyFromParts(
    row.expected_product_code,
    row.expected_color,
    row.expected_size,
    row.expected_brand || row.brand || ""
  );
}

function getIssueReceivedKey(row) {
  return itemKeyFromParts(
    row.received_product_code,
    row.received_color,
    row.received_size,
    row.received_brand || row.brand || ""
  );
}

function getIssueExpectedLabel(row) {
  return itemLabelFromParts(
    row.expected_product_code,
    row.expected_color,
    row.expected_size,
    row.expected_brand || row.brand || ""
  );
}

function getIssueReceivedLabel(row) {
  return itemLabelFromParts(
    row.received_product_code,
    row.received_color,
    row.received_size,
    row.received_brand || row.brand || ""
  );
}

function buildTransferItemAudit(detailData) {
  const requiredRows = Array.isArray(detailData?.officeRequiredItems)
    ? detailData.officeRequiredItems
    : [];
  const pickedRows = Array.isArray(detailData?.warehousePickedItems)
    ? detailData.warehousePickedItems
    : [];
  const transferRows = Array.isArray(detailData?.transferItems)
    ? detailData.transferItems
    : [];
  const movementRows = Array.isArray(detailData?.stockMovements)
    ? detailData.stockMovements
    : [];
  const issueRows = Array.isArray(detailData?.issueRows)
    ? detailData.issueRows
    : [];

  const itemMap = new Map();

  function ensureItem(key, label, base = {}) {
    if (!itemMap.has(key)) {
      itemMap.set(key, {
        key,
        label,
        code: base.code || "-",
        color: base.color || "-",
        size: base.size || "-",
        brand: base.brand || "-",
        requiredQty: 0,
        pickedQty: 0,
        transferQty: 0,
        stockOutQty: 0,
        stockInQty: 0,
        issueCount: 0,
        issueTypes: new Set(),
        result: "OK",
      });
    }

    return itemMap.get(key);
  }

  requiredRows.forEach((row) => {
    const key = itemKeyFromParts(row.product_code, row.color, row.size, row.brand);
    const item = ensureItem(key, itemLabelFromParts(row.product_code, row.color, row.size, row.brand), {
      code: row.product_code,
      color: row.color,
      size: row.size,
      brand: row.brand,
    });
    item.requiredQty += Number(row.required_qty || 0);
  });

  pickedRows.forEach((row) => {
    const key = itemKeyFromParts(row.product_code, row.color, row.size, row.brand);
    const item = ensureItem(key, itemLabelFromParts(row.product_code, row.color, row.size, row.brand), {
      code: row.product_code,
      color: row.color,
      size: row.size,
      brand: row.brand,
    });
    item.pickedQty += Number(row.picked_qty || 0);
  });

  transferRows.forEach((row) => {
    const key = itemKeyFromParts(row.product_code, row.color, row.size, row.brand);
    const item = ensureItem(key, itemLabelFromParts(row.product_code, row.color, row.size, row.brand), {
      code: row.product_code,
      color: row.color,
      size: row.size,
      brand: row.brand,
    });
    item.transferQty += Number(row.qty || 0);
  });

  movementRows.forEach((row) => {
    const key = itemKeyFromParts(row.product_code, row.color, row.size, row.brand);
    const item = ensureItem(key, itemLabelFromParts(row.product_code, row.color, row.size, row.brand), {
      code: row.product_code,
      color: row.color,
      size: row.size,
      brand: row.brand,
    });

    const direction = String(row.direction || "").toUpperCase();
    if (direction === "OUT") item.stockOutQty += Number(row.qty || 0);
    if (direction === "IN") item.stockInQty += Number(row.qty || 0);
  });

  const issueSummaryRows = issueRows.map((row) => {
    const issueType = String(row.discrepancy_type || "").toUpperCase();
    const expectedKey = getIssueExpectedKey(row);
    const receivedKey = getIssueReceivedKey(row);

    if (issueType.includes("EXTRA")) {
      const item = ensureItem(receivedKey, getIssueReceivedLabel(row), {
        code: row.received_product_code,
        color: row.received_color,
        size: row.received_size,
        brand: row.received_brand || row.brand,
      });
      item.issueCount += 1;
      item.issueTypes.add("EXTRA");
    } else if (issueType.includes("MISSING") || issueType.includes("SHORT")) {
      const item = ensureItem(expectedKey, getIssueExpectedLabel(row), {
        code: row.expected_product_code,
        color: row.expected_color,
        size: row.expected_size,
        brand: row.expected_brand || row.brand,
      });
      item.issueCount += 1;
      item.issueTypes.add("MISSING");
    } else if (issueType.includes("WRONG")) {
      const expectedItem = ensureItem(expectedKey, getIssueExpectedLabel(row), {
        code: row.expected_product_code,
        color: row.expected_color,
        size: row.expected_size,
        brand: row.expected_brand || row.brand,
      });
      expectedItem.issueCount += 1;
      expectedItem.issueTypes.add("WRONG_EXPECTED");

      const receivedItem = ensureItem(receivedKey, getIssueReceivedLabel(row), {
        code: row.received_product_code,
        color: row.received_color,
        size: row.received_size,
        brand: row.received_brand || row.brand,
      });
      receivedItem.issueCount += 1;
      receivedItem.issueTypes.add("WRONG_RECEIVED");
    } else {
      const item = ensureItem(expectedKey || receivedKey, getIssueExpectedLabel(row) || getIssueReceivedLabel(row));
      item.issueCount += 1;
      item.issueTypes.add(issueType || "ISSUE");
    }

    return {
      id: row.id,
      type: issueType || "-",
      expectedItem: getIssueExpectedLabel(row),
      receivedItem: getIssueReceivedLabel(row),
      expectedQty: Number(row.expected_qty || 0),
      receivedQty: Number(row.received_qty || 0),
      differenceQty: Number(row.difference_qty || 0),
      reportedBy: row.reported_by_location || "-",
      responsible: row.responsible_location || row.responsible_area || "-",
      status: row.status || "-",
      reason: row.reason || row.note || "-",
    };
  });

  const itemRows = Array.from(itemMap.values()).map((item) => {
    const issueTypes = Array.from(item.issueTypes);

    let result = "OK";
    if (issueTypes.some((x) => x.includes("EXTRA"))) result = "EXTRA ITEM";
    else if (issueTypes.some((x) => x.includes("MISSING"))) result = "MISSING / LOST";
    else if (issueTypes.some((x) => x.includes("WRONG"))) result = "WRONG ITEM";
    else if (item.issueCount > 0) result = "CHECK ISSUE";

    return {
      ...item,
      issueTypes,
      result,
    };
  });

  itemRows.sort((a, b) => {
    if (a.result !== "OK" && b.result === "OK") return -1;
    if (a.result === "OK" && b.result !== "OK") return 1;
    return a.label.localeCompare(b.label);
  });

  return { itemRows, issueSummaryRows };
}

// WAREHOUSE_TRANSFER_DETAIL_ITEM_AUDIT_SUMMARY_V1

export default function WarehouseTransferHistoryPanel({ selectedLocation = "ALL" }) {
  const [status, setStatus] = useState("ALL");
  const [search, setSearch] = useState("");
  const [rows, setRows] = useState([]);
  const [detail, setDetail] = useState(null);
  const [detailData, setDetailData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [error, setError] = useState("");

  async function loadRows() {
    try {
      setLoading(true);
      setError("");

      const params = new URLSearchParams({
        location: selectedLocation || "ALL",
        status,
        limit: "300",
      });

      if (search.trim()) params.set("search", search.trim());

      const res = await fetch(`${API_BASE}/api/erp/transfer-history?${params.toString()}`);
      const data = await res.json().catch(() => ({}));

      if (!res.ok || data.success === false) {
        throw new Error(data.message || "Load transfer history failed");
      }

      setRows(Array.isArray(data.rows) ? data.rows : []);
    } catch (err) {
      setError(err.message || "Load transfer history failed");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }

  async function openDetail(row) {
    try {
      setDetail(row);
      setDetailData(null);
      setDetailLoading(true);

      const res = await fetch(`${API_BASE}/api/erp/transfer-history/${encodeURIComponent(row.id)}/detail`);
      const data = await res.json().catch(() => ({}));

      if (!res.ok || data.success === false) {
        throw new Error(data.message || "Load transfer detail failed");
      }

      setDetailData(data);
    } catch (err) {
      setDetailData({ success: false, message: err.message });
    } finally {
      setDetailLoading(false);
    }
  }

  useEffect(() => {
    loadRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLocation, status]);

  const summary = useMemo(() => {
    return {
      count: rows.length,
      issues: rows.reduce((sum, row) => sum + Number(row.issueCount || 0), 0),
      openIssues: rows.reduce((sum, row) => sum + Number(row.openIssueCount || 0), 0),
    };
  }, [rows]);

  return (
    <section className="whTransferHistory">
      <div className="whTransferHistory__head">
        <div>
          <span>REPORTS / TRANSFER HISTORY</span>
          <h2>Transfer History <span className="whTransferHistory__location">{selectedLocation || "ALL"}</span></h2>
          <p>Track dispatches, receipts and stock movement issues.</p>
        </div>

        <button type="button" onClick={loadRows} disabled={loading}>
          {loading ? "Loading..." : "Refresh"}
        </button>
      </div>

      <div className="whTransferHistory__filters">
        <input
          aria-label="Search transfer history"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search transfer no / batch / location"
          onKeyDown={(e) => {
            if (e.key === "Enter") loadRows();
          }}
        />

        <select aria-label="Transfer status" value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="ALL">All Status</option>
          <option value="APPROVED">Approved</option>
          <option value="DISPATCHED">Dispatched</option>
          <option value="ACCEPTED">Accepted</option>
          <option value="COMPLETED">Completed</option>
          <option value="RECEIVED">Received</option>
        </select>

        <button type="button" onClick={loadRows} disabled={loading}>Search</button>
      </div>

      <div className="whTransferHistory__summary">
        <div><span>Total Transfers</span><b>{summary.count}</b></div>
        <div><span>Total Issues</span><b>{summary.issues}</b></div>
        <div><span>Open Issues</span><b>{summary.openIssues}</b></div>
      </div>

      {error && <div className="whTransferHistory__error">{error}</div>}

      <div className="whTransferHistory__results" aria-busy={loading}>
      <div className="whTransferHistory__tableWrap" tabIndex="0" role="region" aria-label="Transfer history table">
        <table aria-label="Transfer history">
          <thead>
            <tr>
              <th>Transfer No</th>
              <th>From</th>
              <th>To</th>
              <th>Status</th>
              <th>Batch</th>
              <th>Required</th>
              <th>Picked</th>
              <th>Items</th>
              <th>Issues</th>
              <th>Created</th>
              <th>Action</th>
            </tr>
          </thead>

          <tbody>
            {
              rows.map((row) => (
                <tr key={row.id}>
                  <td><b>{row.transferNo}</b></td>
                  <td>{row.fromLocationCode}</td>
                  <td>{row.toLocationCode}</td>
                  <td>
                    <span className={`whTransferHistory__status ${statusClass(row.status)}`}>
                      {row.status}
                    </span>
                  </td>
                  <td>{row.batchNo || "-"}</td>
                  <td>{fmtQty(row.officeRequiredQty)}</td>
                  <td>{fmtQty(row.pickedQty)}</td>
                  <td>{fmtQty(row.totalQty)}</td>
                  <td>
                    {row.issueCount > 0 ? (
                      <b className={row.openIssueCount > 0 ? "isIssueOpen" : "isIssueClosed"}>
                        {row.issueCount} / open {row.openIssueCount}
                      </b>
                    ) : "-"}
                  </td>
                  <td>{fmtDate(row.createdAt)}</td>
                  <td>
                    <button type="button" onClick={() => openDetail(row)}>
                      Detail
                    </button>
                  </td>
                </tr>
              ))
            }
          </tbody>
        </table>
      </div>
      {rows.length === 0 && (
        <div className="whTransferHistory__emptyState" role="status">
          <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M7 3h10l3 5v12H4V8l3-5ZM4 8h16M9 12h6M9 16h4" />
          </svg>
          <strong>{loading ? "Loading transfer history…" : error ? "Transfer history is unavailable" : "No transfers found"}</strong>
          <p>{loading ? "Please wait while the records load." : error ? "Refresh to try again." : "Transfers for this warehouse will appear here. You can also try another search or status."}</p>
        </div>
      )}
      </div>

      {detail && (
        <div className="whTransferHistoryModal" onClick={() => setDetail(null)}>
          <div className="whTransferHistoryModal__body" onClick={(e) => e.stopPropagation()}>
            <div className="whTransferHistoryModal__head">
              <div>
                <span>TRANSFER DETAIL</span>
                <h2>{detail.transferNo}</h2>
                <p>{detail.fromLocationCode} → {detail.toLocationCode}</p>
              </div>

              <button type="button" aria-label="Close transfer detail" onClick={() => setDetail(null)}>×</button>
            </div>

            {detailLoading ? (
              <div className="whTransferHistory__empty">Loading detail...</div>
            ) : detailData?.success === false ? (
              <div className="whTransferHistory__error">{detailData.message}</div>
            ) : detailData ? (
              <EnhancedTransferDetail detailData={detailData} />
            ) : null}
          </div>
        </div>
      )}
    </section>
  );
}


function EnhancedTransferDetail({ detailData }) {
  const { itemRows, issueSummaryRows } = buildTransferItemAudit(detailData);

  return (
    <div className="whTransferHistoryDetailGrid">
      <div className="whTransferHistoryIssueSummary">
        <div className="whTransferHistoryIssueSummary__head">
          <div>
            <h3>Item Check Summary</h3>
            <p>Transfer တစ်ခုထဲက item အလိုက် OK / Extra / Wrong / Missing ကို ဒီ table မှာကြည့်နိုင်ပါတယ်။</p>
          </div>

          <div className="whTransferHistoryIssueSummary__stats">
            <span>Total Items: <b>{itemRows.length}</b></span>
            <span>Issue Items: <b>{itemRows.filter((row) => row.result !== "OK").length}</b></span>
          </div>
        </div>

        <div className="whTransferHistoryDetailTable whTransferHistoryDetailTable--audit">
          <table>
            <thead>
              <tr>
                <th>Result</th>
                <th>Code</th>
                <th>Color</th>
                <th>Size</th>
                <th>Brand</th>
                <th>Required</th>
                <th>Picked</th>
                <th>Transfer Qty</th>
                <th>Stock OUT</th>
                <th>Stock IN</th>
                <th>Issues</th>
              </tr>
            </thead>

            <tbody>
              {itemRows.length === 0 ? (
                <tr>
                  <td colSpan="11">No item audit data</td>
                </tr>
              ) : (
                itemRows.map((row) => (
                  <tr key={row.key} className={row.result !== "OK" ? "hasIssue" : ""}>
                    <td>
                      <span className={`whTransferResultBadge ${row.result === "OK" ? "ok" : "issue"}`}>
                        {row.result}
                      </span>
                    </td>
                    <td>{row.code}</td>
                    <td>{row.color}</td>
                    <td>{row.size}</td>
                    <td>{row.brand}</td>
                    <td>{fmtQty(row.requiredQty)}</td>
                    <td>{fmtQty(row.pickedQty)}</td>
                    <td>{fmtQty(row.transferQty)}</td>
                    <td>{fmtQty(row.stockOutQty)}</td>
                    <td>{fmtQty(row.stockInQty)}</td>
                    <td>
                      {row.issueCount > 0 ? (
                        <b className="isIssueOpen">
                          {row.issueCount} — {row.issueTypes.join(", ")}
                        </b>
                      ) : "-"}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="whTransferHistoryIssueSummary">
        <div className="whTransferHistoryIssueSummary__head">
          <div>
            <h3>Issue Item List</h3>
            <p>Receiver က report တင်ထားသော Extra / Wrong / Missing item များ။</p>
          </div>
        </div>

        <div className="whTransferHistoryDetailTable whTransferHistoryDetailTable--issues">
          <table>
            <thead>
              <tr>
                <th>Issue Type</th>
                <th>Expected Item</th>
                <th>Received Item</th>
                <th>Expected Qty</th>
                <th>Received Qty</th>
                <th>Difference</th>
                <th>Reported By</th>
                <th>Responsible</th>
                <th>Status</th>
                <th>Reason / Note</th>
              </tr>
            </thead>

            <tbody>
              {issueSummaryRows.length === 0 ? (
                <tr>
                  <td colSpan="10">No issue reported for this transfer.</td>
                </tr>
              ) : (
                issueSummaryRows.map((row, index) => (
                  <tr key={row.id || index}>
                    <td><b>{row.type}</b></td>
                    <td>{row.expectedItem}</td>
                    <td>{row.receivedItem}</td>
                    <td>{fmtQty(row.expectedQty)}</td>
                    <td>{fmtQty(row.receivedQty)}</td>
                    <td>{fmtQty(row.differenceQty)}</td>
                    <td>{row.reportedBy}</td>
                    <td>{row.responsible}</td>
                    <td>{row.status}</td>
                    <td>{row.reason}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <DetailTable title="Office Required List" rows={detailData.officeRequiredItems} cols={["product_code","color","size","brand","required_qty"]} />
      <DetailTable title="Warehouse Picked List" rows={detailData.warehousePickedItems} cols={["product_code","color","size","brand","picked_qty","picked_by"]} />
      <DetailTable title="Transfer Items" rows={detailData.transferItems} cols={["product_code","color","size","brand","qty","status"]} />
      <DetailTable title="Stock Movements" rows={detailData.stockMovements} cols={["location_code","product_code","color","size","direction","qty","movement_type","created_by"]} />
      <DetailTable title="Issues / Discrepancies" rows={detailData.issueRows} cols={["discrepancy_type","status","from_location_code","to_location_code","expected_product_code","expected_color","expected_size","expected_qty","received_product_code","received_color","received_size","received_qty"]} />
    </div>
  );
}

function DetailTable({ title, rows = [], cols = [] }) {
  return (
    <div className="whTransferHistoryDetailTable">
      <h3>{title}</h3>
      <div>
        <table>
          <thead>
            <tr>
              {cols.map((col) => <th key={col}>{col}</th>)}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={cols.length}>No data</td></tr>
            ) : rows.map((row, index) => (
              <tr key={row.id || index}>
                {cols.map((col) => <td key={col}>{String(row[col] ?? "-")}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
